import { toPosTotals } from '@/components/pos/posCart'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {ReturnType<typeof toPosTotals> | import('@/types/api').Sale | null} props.totals
 * @param {'compact' | 'default'} [props.variant='default']
 * @param {string} [props.className]
 * @param {string} [props.vatLabel='VAT']
 */
export function PosTotalsBreakdown({
  totals,
  variant = 'default',
  className,
  vatLabel = 'VAT',
}) {
  const resolved = totals && typeof totals === 'object' && 'grand_total' in totals
    ? totals
    : toPosTotals(totals)

  const hasAmount = resolved.subtotal > 0 || resolved.grand_total > 0
  if (!hasAmount) {
    return null
  }

  const showVat = resolved.vat > 0.005
  const showDiscount = resolved.discount > 0.005
  const showTip = resolved.tip > 0.005
  const compact = variant === 'compact'

  return (
    <div
      className={cn(
        'pos-v2-totals-breakdown',
        compact && 'pos-v2-totals-breakdown--compact',
        className,
      )}
      aria-label="Ticket totals"
    >
      <BreakdownRow compact={compact} label="Subtotal" value={resolved.subtotal} />
      {showDiscount ? (
        <BreakdownRow compact={compact} label="Discount" value={-resolved.discount} negative />
      ) : null}
      {showVat ? (
        <BreakdownRow compact={compact} label={vatLabel} value={resolved.vat} />
      ) : null}
      {showTip ? (
        <BreakdownRow compact={compact} label="Tip" value={resolved.tip} />
      ) : null}
      <BreakdownRow
        compact={compact}
        label="Total"
        value={resolved.grand_total}
        emphasis
      />
    </div>
  )
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {number} props.value
 * @param {boolean} [props.compact]
 * @param {boolean} [props.emphasis]
 * @param {boolean} [props.negative]
 */
function BreakdownRow({ label, value, compact = false, emphasis = false, negative = false }) {
  const formatted = `${negative && value < 0 ? '−' : ''}AED ${Math.abs(value).toFixed(2)}`

  return (
    <div
      className={cn(
        'pos-v2-totals-row flex items-baseline justify-between gap-2',
        emphasis && 'pos-v2-totals-row--emphasis',
      )}
    >
      <span className={cn('pos-v2-totals-label', compact && 'text-[10px]')}>{label}</span>
      <span
        className={cn(
          'pos-v2-totals-value font-mono tabular-nums',
          compact ? 'text-[10px]' : 'text-[11px]',
          emphasis && 'font-bold',
          negative && 'text-emerald-700',
        )}
      >
        {formatted}
      </span>
    </div>
  )
}
